<script setup lang="ts">
const props = withDefaults(defineProps<{
    label?: string;
    elapsed?: string;
    elapsedSeconds?: number | null;
    trackElapsed?: boolean;
}>(), {
    label: '[[[Wczytywanie]]]',
    elapsed: '',
    elapsedSeconds: null,
    trackElapsed: false
});

const trackedTenths = ref(0);
const loadingText = ref<HTMLElement | null>(null);
let timer: ReturnType<typeof setInterval> | null = null;
let shimmerAnimation: Animation | null = null;
const effectiveElapsedSeconds = computed(() => props.elapsedSeconds ?? (props.trackElapsed ? trackedTenths.value / 10 : null));

const elapsedText = computed(() =>
{
    if (props.elapsed)
        return props.elapsed;

    if (effectiveElapsedSeconds.value === null)
        return '';

    const total = Math.max(0, effectiveElapsedSeconds.value);

    if (total < 60)
        return `${total.toFixed(1)}s`;

    return `${Math.floor(total / 60)}m ${(total % 60).toFixed(1)}s`;
});

function stopTimer()
{
    if (timer)
        clearInterval(timer);

    timer = null;
}

function startTimer()
{
    stopTimer();
    trackedTenths.value = 0;

    if (props.trackElapsed)
        timer = setInterval(() => trackedTenths.value += 1, 100);
}

function reducedMotion()
{
    return typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

async function startShimmer()
{
    shimmerAnimation?.cancel();
    shimmerAnimation = null;

    if (!props.label || reducedMotion())
        return;

    await nextTick();

    shimmerAnimation = loadingText.value?.animate([
        { backgroundPosition: '100% 0px' },
        { backgroundPosition: '-100% 0px' }
    ], {
        duration: 1400,
        easing: 'linear',
        iterations: Infinity
    }) ?? null;
}

watch(() => props.trackElapsed, startTimer);
watch(() => props.label, startShimmer);
onMounted(() =>
{
    startTimer();
    void startShimmer();
});
onBeforeUnmount(() =>
{
    stopTimer();
    shimmerAnimation?.cancel();
});
</script>

<template>
    <span class="inline-flex max-w-full flex-nowrap items-center justify-center gap-[10px] whitespace-nowrap" role="status" aria-live="polite" data-loading-state>
        <Loader decorative :size="15" class="shrink-0" data-loading-loader />
        <span
            v-if="props.label"
            ref="loadingText"
            class="shrink-0 bg-[linear-gradient(90deg,var(--color-fg-faint)_35%,var(--color-fg)_50%,var(--color-fg-faint)_65%)] bg-[length:200%_100%] bg-clip-text text-[0.8125rem]/normal font-[500] text-transparent dark:bg-[linear-gradient(90deg,var(--color-fg-faint-dark)_35%,var(--color-fg-dark)_50%,var(--color-fg-faint-dark)_65%)]"
            data-loading-label
            data-shimmer-duration="1400"
        >
            {{ $t(props.label) }}
        </span>
        <span v-if="elapsedText" class="shrink-0 font-mono text-[0.75rem]/normal font-[400] tabular-nums text-fg-faint dark:text-fg-faint-dark" data-loading-elapsed>
            {{ elapsedText }}
        </span>
    </span>
</template>
